// Centralized React Query keys for consistent cache management
const PUBLIC_EXPOSED_CLASSIFICATIONS_KEY = [
  'admin',
  'offering-classification',
  'public-exposed',
] as const;

export const queryKeys = {
  /** Admin-only VIS1 moderation reads. Kept separate from public raise caches. */
  offeringClassificationDetail: (slug: string) =>
    ['admin', 'offering-classification', 'detail', slug] as const,
  publicExposedClassificationsAll: PUBLIC_EXPOSED_CLASSIFICATIONS_KEY,
  publicExposedClassifications: (limit: number, offset: number) =>
    [...PUBLIC_EXPOSED_CLASSIFICATIONS_KEY, limit, offset] as const,
  projects: {
    all: ['projects'] as const,
    byUser: ['projects', 'byUser'] as const,
    byOrg: (orgId: string) => ['projects', 'byOrg', orgId] as const,
    details: (id: string) => ['projects', 'details', id] as const,
    public: ['projects', 'public'] as const,
    approved: ['projects', 'approved'] as const,
    pending: ['projects', 'pending'] as const,
    filters: (filters: Record<string, unknown>) => ['projects', 'list', filters] as const,
  },
  user: {
    all: ['user'] as const,
    current: ['user', 'current'] as const,
    profile: (id: string) => ['user', 'profile', id] as const,
    organization: ['user', 'organization'] as const,
  },
  organization: {
    all: ['organizations'] as const,
    byName: (name: string) => ['organization', 'byName', name] as const,
    byId: (orgId: string) => ['organization', 'byId', orgId] as const,
    members: (identifier: string) => ['organization', identifier, 'members'] as const,
    projects: (identifier: string) => ['organization', identifier, 'projects'] as const,
    details: (identifier: string) => ['organization', identifier, 'details'] as const,
    analytics: {
      summary: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'summary', dateRange] as const,
      memberActivity: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'member-activity', dateRange] as const,
      projectViews: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'project-views', dateRange] as const,
      timeline: (identifier: string, dateRange: string, granularity: string) =>
        ['organization', identifier, 'analytics', 'timeline', dateRange, granularity] as const,
      securityEvents: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'security-events', dateRange] as const,
      shareMetrics: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'share-metrics', dateRange] as const,
      userTimeline: (identifier: string, userId: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'user-timeline', userId, dateRange] as const,
      fileActivity: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'file-activity', dateRange] as const,
      emailStats: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'email-stats', dateRange] as const,
      chatbotUsage: (identifier: string, dateRange: string) =>
        ['organization', identifier, 'analytics', 'chatbot-usage', dateRange] as const,
    },
  },
  dataroom: {
    all: ['dataroom'] as const,
    byProject: (projectId: string) => ['dataroom', projectId] as const,
    files: (projectId: string) => ['dataroom', 'files', projectId] as const,
    // Corpus file notes — list of notes keyed by (projectId, fileId), where
    // fileId is the files.id Postgres UUID primary key (L7 logical-ID
    // boundary, design §6.10 — no filename or path in any cache key).
    // Shares the underlying file_notes table with knowledgeRepo.fileNotes
    // but uses a distinct cache entry because the dataroom authorizes via
    // assertProjectAccess while the knowledge-repo path authorizes via org
    // scoping; the same file may be visible under different rules.
    fileNotes: (projectId: string, fileId: string) =>
      ['dataroom', 'fileNotes', projectId, fileId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    unread: ['notifications', 'unread'] as const,
    owner: {
      /** Broad invalidation seam for every owner notification projection. */
      all: ['notifications', 'owner'] as const,
      list: (limit: number, offset: number) =>
        ['notifications', 'owner', 'list', limit, offset] as const,
      unread: ['notifications', 'owner', 'unread'] as const,
    },
  },
  shares: {
    all: ['shares'] as const,
    // Base keys for broad invalidation (no pagination params - matches all paginated queries)
    outgoingBase: () => ['shares', 'outgoing'] as const,
    incomingBase: () => ['shares', 'incoming'] as const,
    projectBase: (projectId: string) => ['shares', 'project', projectId] as const,
    historyBase: (projectId: string) => ['shares', 'history', projectId] as const,
    // Parameterized keys for specific queries
    outgoing: (page?: number, limit?: number, status?: string[]) => ['shares', 'outgoing', page, limit, status] as const,
    incoming: (page?: number, limit?: number, status?: string[]) =>
      ['shares', 'incoming', page, limit, status] as const,
    project: (projectId: string, page?: number, limit?: number, status?: string[]) => ['shares', 'project', projectId, page, limit, status] as const,
    history: (projectId: string, page?: number) => ['shares', 'history', projectId, page] as const,
    views: (projectId: string) => ['shares', 'views', projectId] as const,
    viewStats: (projectId: string) => ['shares', 'viewStats', projectId] as const,
    organizations: (query?: string) => ['shares', 'organizations', query] as const,
    users: (query: string) => ['shares', 'users', query] as const,
    settings: (orgId: string) => ['organization', orgId, 'share-settings'] as const,
    activeStatus: (projectIds: string[]) => ['shares', 'activeStatus', projectIds.sort().join(',')] as const,
  },
  documentStudio: {
    all: ['documentStudio'] as const,
    documents: ['documentStudio', 'documents'] as const,
    document: (id: string) => ['documentStudio', 'document', id] as const,
    editorConfig: (id: string) => ['documentStudio', 'editorConfig', id] as const,
    versions: (id: string) => ['documentStudio', 'versions', id] as const,
    knowledge: ['documentStudio', 'knowledge'] as const,
    templates: ['documentStudio', 'templates'] as const,
    session: (documentId: string) => ['documentStudio', 'session', documentId] as const,
    chatMessages: (sessionId: string) => ['documentStudio', 'chatMessages', sessionId] as const,
  },
  knowledgeRepo: {
    status: ['knowledge-repo', 'status'] as const,
    filterOptions: ['knowledge-repo', 'filter-options'] as const,
    files: (params: Record<string, unknown>) => ['knowledge-repo', 'files', params] as const,
    knowledgeBase: {
      all: ['knowledge-repo', 'knowledge-base'] as const,
      list: (params: Record<string, unknown>) => ['knowledge-repo', 'knowledge-base', params] as const,
    },
    file: (fileId: string) => ['knowledge-repo', 'file', fileId] as const,
    companyProfile: ['knowledge-repo', 'company-profile'] as const,
    similar: (fileId: string) => ['knowledge-repo', 'similar', fileId] as const,
    fileIntelligence: (fileId: string) => ['knowledge-repo', 'file-intelligence', fileId] as const,

    // Deal intelligence keys
    deals: (params?: Record<string, unknown>) => ['knowledge-repo', 'deals', params] as const,
    deal: (dealId: string) => ['knowledge-repo', 'deal', dealId] as const,
    dealSections: (dealId: string) => ['knowledge-repo', 'deal', dealId, 'sections'] as const,
    dealFiles: (dealId: string) => ['knowledge-repo', 'deal', dealId, 'files'] as const,
    // `showAllOrgs` is part of the key because an admin's cross-org similar-deals
    // view must not share cache entries with the same admin's own-org view.
    // Without this, toggling the admin `showAllOrgs` switch would reuse stale
    // org-scoped results until the stale time elapses.
    similarDeals: (dealId: string, showAllOrgs: boolean = false) =>
      ['knowledge-repo', 'similar-deals', dealId, showAllOrgs] as const,
    dealComparison: (dealIdA: string, dealIdB: string) =>
      ['knowledge-repo', 'deal-comparison', dealIdA, dealIdB] as const,
    fileSections: (fileId: string) => ['knowledge-repo', 'file-sections', fileId] as const,

    // Phase 6 P6-T4 — reprocess job-status polling.
    // Keyed by jobId so distinct jobs never share cache entries, and so the
    // `useJobStatus` hook can cleanly disable itself when jobId is null.
    jobStatus: (jobId: string) => ['knowledge-repo', 'job-status', jobId] as const,

    // Phase 6 P6-T6 — admin review queue (deals in 'partial' | 'user_edited' state).
    reviewQueue: ['knowledge-repo', 'deals', 'review-queue'] as const,

    // File notes (multi-note feature — Document Hub path). `showAll` is part
    // of the key because an admin's cross-org view must not share cache
    // entries with the same admin's own-org view.
    fileNotes: (fileId: string, showAll: boolean) =>
      ['knowledge-repo', 'file', fileId, 'notes', showAll ? 'all' : 'org'] as const,

    // Tier 3 — deal-association review flag surface (dealName/propertyName/
    // propertyAddress corrections). Keyed by the full params object like
    // `files` above, so distinct pages/projectId/showAll scopes never
    // collide in the cache.
    dealReviewFlags: (params: Record<string, unknown>) =>
      ['knowledge-repo', 'deal-review-flags', params] as const,
  },
  // Quant Risk Monitor (Phase-4 task 4.3). Hierarchical namespace over the `/api/quant`
  // read surface: fleet board, composed RiskRunView, per-run artifacts, and bounded
  // path-tensor aggregates. A pinned/latest risk-run is keyed `'pinned'` (distinct cache
  // entry from any historical `runId`); a tensor `path` drilldown carries its index so
  // distinct sampled paths never collide. Run artifacts are content-addressed by `runId`
  // (byte-deterministic) — safe to cache long.
  quant: {
    all: ['quant'] as const,
    fleet: () => ['quant', 'fleet'] as const,
    riskRun: (projectId: string, runId?: string) =>
      ['quant', 'riskRun', projectId, runId ?? 'pinned'] as const,
    artifact: (projectId: string, runId: string, artifact: string) =>
      ['quant', 'artifact', projectId, runId, artifact] as const,
    tensor: (projectId: string, runId: string, kind: string, index?: number) =>
      ['quant', 'tensor', projectId, runId, kind, index ?? null] as const,
    // Async compute plane (task 4.2b). A heavy op submits → 202 + jobId; the FE polls coarse
    // status and (for the 5 uncached heavy ops) fetches the persisted OperationResult. Keyed by
    // jobId (the single Node job-of-record), NOT by projectId — a job outlives a deal selection.
    jobs: {
      status: (jobId: string) => ['quant', 'jobs', 'status', jobId] as const,
      result: (jobId: string) => ['quant', 'jobs', 'result', jobId] as const,
    },
  },
  // Quant Portfolio Aggregation (S6, spec S6-portfolio.md §3). The fund-level joint re-sim read
  // surface. `run` keys the persisted/read-through portfolio run per tenant org (BG-8/BG-12 — the
  // canonical fund number is org-scoped, distinct from any per-deal run); `deal` keys the per-deal
  // summary join (BG-11 — standalone metrics / base_nav / loss_unit powering the ledger drawers +
  // tri-typed zeros). Both are NODE-serve-plane-deferred today; keyed here so they light up when the
  // read paths land without a key rewrite. The live V1 run rides the async `quant.jobs.*` plane.
  quantPortfolio: {
    all: ['quantPortfolio'] as const,
    run: (orgId: string) => ['quantPortfolio', 'run', orgId] as const,
    deal: (dealId: string) => ['quantPortfolio', 'deal', dealId] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
